import React, { useState } from "react";
import { Field, useForm } from "react-final-form";
import MuiButton from "@material-ui/core/Button";
import { Alert } from "@material-ui/lab";
import { useMutation } from "@tanstack/react-query";
import { useFormState as useFinalFormState } from "react-final-form";
import { useFieldArray } from "react-final-form-arrays";
import { axiosClient } from "../../utils/axiosClient";
import TextInput from "../../Shared/TextInput";
import { Trans } from "../../utils/i18n";
import { extractHouseNumber } from "../../utils/extractHouseNumber";
import { OrderEntryConfigReturnOrderDetailsSchema } from "../../config/orderEntryReturn";

const EnterOrderDetails = React.memo(
    function () {
        const [error, setError] = useState(false);
        const formState = useFinalFormState();
        const verfiyOrderMutation = useMutation({
            mutationFn: (data) =>
                axiosClient.post(route("order.return.verfiy"), data),
        });
        const form = useForm();
        const fieldArray = useFieldArray("products");

        const isValid =
            OrderEntryConfigReturnOrderDetailsSchema.safeParse(
                formState.values
            ).success;

        const onEnter = (event) => {
            event.preventDefault();
            if (!isValid) return;

            // Remove all existing return items
            fieldArray.fields.removeBatch(
                fieldArray.fields.map((field, index) => index)
            );

            verfiyOrderMutation.mutate(
                {
                    ordernumber: formState.values.ordernumber,
                    email: formState.values.orderEmail,
                },
                {
                    onSuccess: (data) => {
                        setError(null);

                        // Set parent order number
                        form.change("parent_order_number", data?.data?.number);

                        // Add new return items
                        data?.data?.orderdetails?.forEach((orderDetail) =>
                            fieldArray.fields.push({
                                ...orderDetail.product,
                                title: orderDetail.product.description,
                                maxQuantity: orderDetail.quantity,
                                amount: undefined,
                                problem_1: undefined,
                                isReturn: false,
                            })
                        );

                        // Prefill delivery address
                        const street =
                            data?.data?.deliveryAddress?.street ?? undefined;
                        const houseNumber = street
                            ? extractHouseNumber(street)
                            : undefined;
                        const streetWithoutHouseNumber = street
                            ? street.replace(houseNumber, "").trim()
                            : undefined;

                        form.change(
                            "address1",
                            data?.data?.deliveryAddress?.name
                        );
                        form.change(
                            "email",
                            data?.data?.deliveryAddress?.email
                        );
                        form.change(
                            "phone",
                            data?.data?.deliveryAddress?.phone
                        );
                        form.change("zip", data?.data?.deliveryAddress?.zip);
                        form.change("city", data?.data?.deliveryAddress?.city);
                        form.change("street", streetWithoutHouseNumber);
                        form.change("housenumber", houseNumber);
                        form.change(
                            "country_id",
                            data?.data?.deliveryAddress?.country?.id
                        );
                    },
                    onError: (error) => {
                        setError(
                            error?.response?.data?.message ?? (
                                <Trans i18nKey="returnOrder.errors.orderCouldNotBeVerified">
                                    Order could not be verified.
                                </Trans>
                            )
                        );
                    },
                }
            );
        };

        return (
            <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field
                        name="ordernumber"
                        type="text"
                        component={TextInput}
                        field={{
                            label: (
                                <Trans i18nKey="ordernumber">
                                    Order Number
                                </Trans>
                            ),
                        }}
                        onEnter={onEnter}
                    />
                    <Field
                        name="orderEmail"
                        type="email"
                        component={TextInput}
                        field={{
                            label: <Trans i18nKey="email">E-Mail</Trans>,
                        }}
                        onEnter={onEnter}
                    />
                    <div className="col-start-1 sm:col-start-2">
                        <MuiButton
                            variant="contained"
                            color={"secondary"}
                            onClick={onEnter}
                            style={{ width: "100%" }}
                            disabled={!isValid}
                        >
                            <Trans i18nKey="common.search">Search</Trans>
                        </MuiButton>
                    </div>
                </div>

                {error && <Alert severity="error">{error}</Alert>}
            </div>
        );
    },
    function areEqual(prevProps, nextProps) {
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);

export default EnterOrderDetails;
